'use client'

import { type FC } from 'react'
import { useHits } from 'react-instantsearch'
import { JsonLd } from 'react-schemaorg'
import {
  type ItemList,
  type ItemListOrderType,
  type ListItem,
} from 'schema-dts'

import {
  type AlgoliaRecord,
  algoliaRecordAccessorGetDeeplink,
} from '../../../algolia'
import { useGlobalConfigContext } from '../../../global-config'
import { ProductVolumePricingContextProvider } from '../../../product'
import {
  mapAlgoliaVolumeBasedPrices,
  ProductSearchResultItem,
  ProductSearchResultItemContextProvider,
  useProductSearchResultsGtmTracker,
} from '../../../product-search'

const getItemListOrder = (index: string): ItemListOrderType | undefined => {
  if (index.endsWith('_asc')) {
    return 'https://schema.org/ItemListOrderAscending'
  }

  if (index.endsWith('_desc')) {
    return 'https://schema.org/ItemListOrderDescending'
  }

  return undefined
}

export const CmsAlgoliaHits: FC = () => {
  const { items, results } = useHits<AlgoliaRecord>()

  const { shopUrl } = useGlobalConfigContext()

  useProductSearchResultsGtmTracker(results)

  return (
    <>
      <ul className="grid gap-9">
        {items.map((item) => (
          <li key={item.id}>
            <ProductSearchResultItemContextProvider algoliaRecord={item}>
              <ProductVolumePricingContextProvider
                currentVariantUpid={item.upid}
                volumeBasedPrices={mapAlgoliaVolumeBasedPrices(
                  item.best_offer?.volume_based_prices ?? [],
                )}
              >
                <ProductSearchResultItem />
              </ProductVolumePricingContextProvider>
            </ProductSearchResultItemContextProvider>
          </li>
        ))}
      </ul>
      <JsonLd<ItemList>
        item={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: items.map((item, index): ListItem => ({
            '@type': 'ListItem',
            position: index + 1,
            url: new URL(
              algoliaRecordAccessorGetDeeplink({
                record: item,
              }),
              shopUrl,
            ).href,
          })),
          itemListOrder: results && getItemListOrder(results.index),
        }}
      />
    </>
  )
}
